import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { api, BracketStage, Feeders, Match, TimbaPrediction } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { TimbaTournaments } from '../components/TimbaTournaments';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { Muted } from '../components/ui';
import { getIdentity } from '../lib/identity';
import { buildGraph, bracketHalf, realAdvancer } from '../lib/simulate';
import { formatPick, potentialMultipliers } from '../lib/timba';
import { timbaBracketSlots } from '../lib/timbaBracket';
import { useStore } from '../stores/store';
import { TimbaHelp } from '../components/TimbaHelp';

const LOCK_MS = 2 * 3600 * 1000;
const SPLIT_ROUNDS = ['r32', 'r16', 'qf', 'sf'];
const CENTER_ROUNDS = ['third', 'final'];

type PredMap = Record<number, TimbaPrediction>;

function TimbaBracket({ stages, feeders, onHelp }: { stages: BracketStage[]; feeders: Feeders; onHelp: () => void }) {
  const pub = useMemo(() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  }, []);
  const [preds, setPreds] = useState<PredMap>({});
  // Booster/bonus POTENCIAL (si tus pronósticos se cumplen), para los que aún no se confirmaron.
  const potential = useMemo(
    () => potentialMultipliers(new Map(Object.entries(preds).map(([k, v]) => [Number(k), v])), feeders),
    [preds, feeders],
  );

  useEffect(() => {
    if (!pub) return;
    let alive = true;
    api
      .timbaPredictions(pub)
      .then((d) => {
        if (!alive) return;
        const map: PredMap = {};
        d.predictions.forEach((p) => (map[p.num] = p));
        setPreds(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [pub]);

  const g = useMemo(() => buildGraph(stages), [stages]);
  const slots = useMemo(() => timbaBracketSlots(stages, preds), [stages, preds]);
  const stageBy = (key: string) => stages.find((s) => s.key === key);

  const cell = (m: Match) => {
    const [s1, s2] = slots.get(m.num as number) ?? [
      { team: null, label: '', confirmed: false },
      { team: null, label: '', confirmed: false },
    ];
    const p = preds[m.num as number];
    const finished = m.status === 'finished' && m.score != null;
    const pen = m.score?.pen ?? null;                  // tanda: [pa, pb] o null
    const adv = finished ? realAdvancer(m.score) : null; // 1|2 = quién avanzó (90'→alargue→penales)
    const open = !!m.kickoffUtc && Date.now() < new Date(m.kickoffUtc).getTime() - LOCK_MS && !finished;
    const hit = finished && p?.points != null && p.points > 0; // "embocado": tu pronóstico sumó
    // Rivales tentativos: hay equipos pero alguno todavía no está confirmado (proyectado o por pick).
    const teamsKnown = s1.team !== null && s2.team !== null;
    const tentative = (!!s1.team && !s1.confirmed) || (!!s2.team && !s2.confirmed);
    const status = finished
      ? 'Finalizado'
      : open
        ? tentative ? 'Rivales tentativos' : 'Abierto'
        : 'Cerrado';

    // Booster/bonus: confirmado (backend, partido jugado) o potencial (cliente). Solo se muestran
    // si el cruce ya tiene los DOS equipos definidos (reales o tentativos).
    const confirmed = finished && p?.points != null;
    const booster = confirmed ? p!.booster ?? 1 : potential.get(m.num as number)?.booster ?? 1;
    const bonus = confirmed ? p!.bonus ?? 0 : potential.get(m.num as number)?.bonus ?? 0;
    const showBadges = teamsKnown && (booster > 1 || bonus > 0);

    return (
      <Cell key={m.id} to={`/match/${m.id}`} $open={open} $picked={!!p} $hit={hit}>
        <Row className={s1.team && !s1.confirmed ? 'tent' : ''}>
          <Flag team={s1.team} size={16} />
          <span className={s1.team ? 'nm' : 'nm ph'}>{s1.team ? teamName(s1.team) : s1.label}</span>
          {finished && <span className={`go ${adv === 1 ? 'win' : ''}`}>{m.score!.ft[0]}{pen && <em>({pen[0]})</em>}</span>}
        </Row>
        <Row className={s2.team && !s2.confirmed ? 'tent' : ''}>
          <Flag team={s2.team} size={16} />
          <span className={s2.team ? 'nm' : 'nm ph'}>{s2.team ? teamName(s2.team) : s2.label}</span>
          {finished && <span className={`go ${adv === 2 ? 'win' : ''}`}>{m.score!.ft[1]}{pen && <em>({pen[1]})</em>}</span>}
        </Row>
        {showBadges && (
          <Badges
            $potential={!confirmed}
            role="button"
            title="¿Qué significan? Tocá para ver la ayuda"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onHelp();
            }}
          >
            {booster > 1 && <span className="boost">×{booster}</span>}
            {bonus > 0 && <span className="llave">🔑 +{bonus}</span>}
            {!confirmed && <span className="tag">potencial</span>}
          </Badges>
        )}
        <Foot $picked={!!p} $hit={hit}>
          <span className="pick">{p ? `Tu pick: ${formatPick(p, s1.team, s2.team)}` : open ? 'Tocá para pronosticar' : '—'}</span>
          {finished && p?.points != null ? (
            <span className={`pts ${p.points > 0 ? 'hit' : ''}`}>+{p.points}</span>
          ) : (
            <span className={`st ${open ? 'on' : ''}`}>{status}</span>
          )}
        </Foot>
      </Cell>
    );
  };

  return (
    <>
      {SPLIT_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        const left = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'A');
        const right = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'B');
        return (
          <div key={key}>
            <RoundLabel>{st.label}</RoundLabel>
            <TwoCol>
              <div>{left.map(cell)}</div>
              <div>{right.map(cell)}</div>
            </TwoCol>
          </div>
        );
      })}
      {CENTER_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        return (
          <div key={key}>
            <RoundLabel>{st.label}</RoundLabel>
            {st.matches.map(cell)}
          </div>
        );
      })}
    </>
  );
}

export function Timba() {
  const state = useAsync(() => api.bracket(), [], '/bracket');
  const [helpOpen, setHelpOpen] = useState(false);
  const startRoute = useStore((s) => s.startRoute);
  const setStartRoute = useStore((s) => s.setStartRoute);
  const homeStart = startRoute === '/'; // el usuario optó por arrancar en "Hoy" (la Timba es el default)

  return (
    <>
      <TopBar>
        <button type="button" className={!homeStart ? 'pin on' : 'pin'} onClick={() => setStartRoute(homeStart ? '/timba' : '/')}>
          📌 {homeStart ? 'Arrancar en la Timba' : 'Arrancás en la Timba'}
        </button>
        <button type="button" className="help" onClick={() => setHelpOpen(true)}>ℹ️ Cómo se juega</button>
      </TopBar>

      <Intro>
        <h2>🎲 Timba</h2>
        <Muted style={{ lineHeight: 1.55 }}>
          Pronosticá cada partido de la fase de llave: tocá un cruce, cargá el marcador y (si es empate) quién
          gana los penales. Sumás puntos por acertar el resultado y el marcador. Armá torneos privados con tus
          amigos o competí en el ranking global.
        </Muted>
      </Intro>

      <TimbaTournaments />

      <Async state={state}>{(d) => <TimbaBracket stages={d.stages} feeders={d.feeders} onHelp={() => setHelpOpen(true)} />}</Async>

      {helpOpen && <TimbaHelp onClose={() => setHelpOpen(false)} />}
    </>
  );
}

const Intro = styled.div`
  margin-bottom: 14px;
  h2 { margin: 0 0 6px; font-size: 1.4rem; }
`;

const TopBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  button {
    flex: 1;
    padding: 9px 10px;
    border-radius: 999px;
    font-size: 0.82rem;
    font-weight: 700;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: none;
    color: ${({ theme }) => theme.colors.text};
  }
  .pin.on { background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText}; border-color: ${({ theme }) => theme.colors.accent}; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: start;
`;

const RoundLabel = styled.div`
  text-align: center;
  font-size: 1.05rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text};
  margin: 18px 0 10px;
`;

// $picked: tengo pronóstico → card celeste (con franja inferior celeste). $hit: emboqué (sumó
// puntos) → mismo fondo celeste pero el FILETE (borde) en dorado + glow, para que salte la ganadora.
const Cell = styled(Link)<{ $open: boolean; $picked: boolean; $hit: boolean }>`
  display: block;
  overflow: hidden;
  background: ${({ theme, $picked }) => ($picked ? 'rgba(56, 189, 248, 0.13)' : theme.colors.surface)};
  border: 1.5px solid ${({ theme, $open, $picked, $hit }) =>
    $hit ? theme.colors.hit : $picked ? theme.colors.pick : $open ? theme.colors.accent : theme.colors.border};
  border-left: ${({ theme, $picked, $hit }) =>
    $hit ? `5px solid ${theme.colors.hit}` : $picked ? `4px solid ${theme.colors.pick}` : '1px solid transparent'};
  border-radius: 10px;
  padding: 9px 10px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text};
  ${({ $hit, theme }) => $hit && `box-shadow: 0 0 0 1px ${theme.colors.hit}, 0 4px 16px ${theme.colors.hit}55;`}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  .nm { flex: 1; font-weight: 600; font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .nm.ph { color: ${({ theme }) => theme.colors.textMuted}; font-style: italic; font-weight: 500; font-size: 0.8rem; }
  .go { flex: 0 0 auto; min-width: 20px; text-align: right; font-size: 1.4rem; font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; color: ${({ theme }) => theme.colors.textMuted}; }
  .go.win { color: ${({ theme }) => theme.colors.text}; }
  .go em { font-size: 0.6em; font-style: normal; font-weight: 800; margin-left: 1px; color: ${({ theme }) => theme.colors.accent}; }
  & + & { margin-top: 3px; }
  /* Rival tentativo (proyectado del grupo): en gris hasta que se confirme. */
  &.tent { filter: grayscale(1); opacity: 0.5; }
`;

// Badges de booster (×N, ámbar) y bonus de llave (🔑, azul). Punteado = potencial; sólido = confirmado.
const Badges = styled.div<{ $potential: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  cursor: pointer;
  opacity: ${({ $potential }) => ($potential ? 0.8 : 1)};
  span {
    font-size: 0.92rem;
    font-weight: 900;
    padding: 4px 10px;
    border-radius: 8px;
    border: ${({ $potential }) => ($potential ? '1.5px dashed' : '1.5px solid')};
  }
  .boost { color: ${({ theme }) => theme.colors.boost}; border-color: ${({ theme }) => theme.colors.boost}; }
  .llave { color: ${({ theme }) => theme.colors.gold}; border-color: ${({ theme }) => theme.colors.gold}; }
  .tag { color: ${({ theme }) => theme.colors.textMuted}; border-color: transparent; font-weight: 700; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.5px; padding-left: 0; }
`;

// Franja inferior: si hay pronóstico, es una banda CELESTE que sangra a los bordes (pick + puntos);
// si no, un pie liso con separador. El puntaje embocado va en una píldora DORADA (bien evidente).
const Foot = styled.div<{ $picked: boolean; $hit: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  ${({ $hit, theme }) =>
    $hit
      ? `margin: 8px -10px -9px; padding: 8px 10px; background: ${theme.colors.pick};`
      : `margin-top: 8px; padding-top: 7px; border-top: 1px solid ${theme.colors.border};`}
  .pick {
    font-size: 0.78rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    color: ${({ $hit, $picked, theme }) => ($hit ? '#06283a' : $picked ? theme.colors.pick : theme.colors.textMuted)};
    font-weight: ${({ $picked }) => ($picked ? 800 : 400)};
  }
  .st {
    font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
    color: ${({ $hit, theme }) => ($hit ? '#06283a' : theme.colors.textMuted)};
  }
  .st.on { color: ${({ $hit, theme }) => ($hit ? '#06283a' : theme.colors.accent)}; }
  .pts {
    flex: 0 0 auto; font-weight: 900; font-size: 0.92rem;
    padding: 2px 9px; border-radius: 999px;
    background: rgba(3, 30, 44, 0.16); color: #06283a;
  }
  .pts.hit {
    background: ${({ theme }) => theme.colors.hit}; color: #3a2a00;
    font-size: 1.1rem; box-shadow: 0 1px 6px ${({ theme }) => theme.colors.hit}66;
  }
`;
